import { type WeatherLocation, DEFAULT_LOCATION } from "@/lib/weatherLocations";
import { fixCityCoords, isSameCity } from "@/lib/citySearch";
import { supabase } from "@/integrations/supabase/client";

/**
 * Ciudades guardadas del selector del tiempo.
 *
 * La verdad vive en la CUENTA (`profiles.weather_cities`): el mismo usuario entra
 * desde el móvil y el PC y espera encontrarse las mismas ciudades. El localStorage
 * es solo una caché DE ESE USUARIO en ESE aparato, para pintar la lista sin esperar
 * a la red.
 *
 * ⚠️ POR QUÉ LA CACHÉ VA CON EL user_id DELANTE (25-08-2026).
 *
 * Antes la caché era una sola clave para todo el navegador, `ipm_weather_cities`,
 * y `signOut` no la borraba. Encima, al hidratar se hacía la UNIÓN de la cuenta y
 * de esa caché, y una cuenta recién creada —lista vacía en la base— se SEMBRABA
 * con lo que el aparato tuviera guardado. Resultado: cada cuenta nueva que
 * estrenaba ese navegador heredaba las ciudades de la anterior, se le escribían en
 * su perfil, y borrarlas no servía de nada porque la caché volvía a meterlas en el
 * siguiente arranque.
 *
 * Se vio en producción el 25-08-2026: cinco perfiles con el MISMO juego de
 * ciudades de prueba (Shenzhen, Pekín, Saint-Tropez, Milán, Sofía, Oporto), los
 * cinco clavados en el tope de 10, dos de ellos idénticos carácter a carácter —
 * mientras que los once usuarios de verdad tenían «Almería» y poco más. Franco Joy,
 * creado dos días antes, tenía las diez.
 *
 * Las reglas ahora:
 *   · la caché se guarda en `ipm_weather_cities:<user_id>`, así que dos cuentas en
 *     el mismo navegador no se ven la una a la otra;
 *   · la clave global vieja se BORRA y no se lee nunca: migrarla sería repetir el
 *     contagio;
 *   · si la cuenta se lee bien, la cuenta MANDA. Vacía significa vacía: una cuenta
 *     nueva empieza sin nada, no con lo que hubiera en el aparato;
 *   · al cerrar sesión o cambiar de usuario se tira el estado en memoria.
 */

const KEY_PREFIX = "ipm_weather_cities";
/** La clave de todo el navegador, la que contagiaba. Solo se borra. */
const LEGACY_KEY = KEY_PREFIX;
export const MAX_SAVED_CITIES = 10;

const cacheKey = (uid: string) => `${KEY_PREFIX}:${uid}`;

/* La columna weather_cities aún no existe en los tipos generados (se regeneran desde
   producción cuando se publique la migración): acceso tipado a mano SOLO para ella. */
const profilesWeatherCities = () =>
  supabase.from("profiles") as unknown as {
    select: (cols: string) => {
      eq: (col: string, val: string) => {
        maybeSingle: () => Promise<{ data: { weather_cities?: unknown } | null; error: unknown }>;
      };
    };
    update: (values: Record<string, unknown>) => {
      eq: (col: string, val: string) => Promise<{ error: unknown }>;
    };
  };

function isCity(v: unknown): v is WeatherLocation {
  const c = v as WeatherLocation;
  return !!c && typeof c.city === "string" && c.city.trim().length > 0;
}

/** Tira la clave global vieja. Se llama una vez al cargar el módulo. */
export function dropLegacyCache(): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Modo incógnito: no había nada que tirar.
  }
}

/** Caché de ESTE usuario en ESTE aparato. Sin usuario no hay caché que valga. */
export function loadSavedCities(uid: string | null): WeatherLocation[] {
  if (!uid) return [DEFAULT_LOCATION];
  try {
    const raw = localStorage.getItem(cacheKey(uid));
    if (!raw) return [DEFAULT_LOCATION];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [DEFAULT_LOCATION];
    // fixCityCoords: las guardadas con coordenadas corruptas del geocoding
    // (La Mojonera, ver GEO_OVERRIDES) se reparan al leer.
    const clean = parsed.filter(isCity).map(fixCityCoords).slice(0, MAX_SAVED_CITIES);
    return clean.length > 0 ? clean : [DEFAULT_LOCATION];
  } catch {
    return [DEFAULT_LOCATION];
  }
}

export function persistSavedCities(uid: string | null, cities: WeatherLocation[]) {
  if (!uid) return;
  try {
    localStorage.setItem(cacheKey(uid), JSON.stringify(cities.slice(0, MAX_SAVED_CITIES)));
  } catch {
    // Modo incógnito o almacenamiento lleno: la lista sigue viva en memoria.
  }
}

/**
 * Lista de la cuenta. `ok: false` significa «no se pudo LEER» (sin sesión, error,
 * excepción) y es distinto de `ok: true, cities: []` («leído: nunca guardó nada»).
 * La diferencia importa: tras una lectura fallida NO se puede escribir la cuenta,
 * porque se machacaría la lista buena con la caché de este aparato.
 */
export async function loadRemoteCities(uid: string): Promise<{ ok: boolean; cities: WeatherLocation[] }> {
  try {
    if (!uid) return { ok: false, cities: [] };
    const { data, error } = await profilesWeatherCities().select("weather_cities").eq("user_id", uid).maybeSingle();
    if (error || !data) return { ok: false, cities: [] };
    const raw = data.weather_cities;
    if (!Array.isArray(raw)) return { ok: true, cities: [] };
    return { ok: true, cities: raw.filter(isCity).map(fixCityCoords).slice(0, MAX_SAVED_CITIES) };
  } catch {
    return { ok: false, cities: [] };
  }
}

export async function persistRemoteCities(uid: string | null, cities: WeatherLocation[]): Promise<void> {
  try {
    if (!uid) return;
    await profilesWeatherCities().update({ weather_cities: cities.slice(0, MAX_SAVED_CITIES) }).eq("user_id", uid);
  } catch {
    // Sin red: la caché local sigue mandando en este aparato hasta la próxima.
  }
}

/**
 * Mete la ciudad al principio si no estaba. Se usa también al montar con la ciudad
 * activa: sin eso, quien ya tuviera guardada una ciudad del catálogo viejo abriría
 * el selector y no vería ninguna, porque el catálogo ya no se pinta.
 */
export function addCity(cities: WeatherLocation[], city: WeatherLocation): WeatherLocation[] {
  if (cities.some((c) => isSameCity(c, city))) return cities;
  return [city, ...cities].slice(0, MAX_SAVED_CITIES);
}

export function removeCity(cities: WeatherLocation[], city: WeatherLocation): WeatherLocation[] {
  return cities.filter((c) => !isSameCity(c, city));
}

/**
 * Cuenta + caché DE ESE MISMO USUARIO en este aparato. Manda el orden de la cuenta
 * y detrás va lo que este navegador tuviera suyo de antes, que es lo que aún no ha
 * llegado a subir (una ciudad añadida sin red, por ejemplo).
 *
 * Ojo con lo que esto NO es: no se fusiona nunca con la caché de OTRO usuario. Esa
 * unión —cuando la caché era una sola para todo el navegador— es la que repartió
 * las mismas diez ciudades por cinco perfiles distintos.
 */
export function mergeCities(remote: WeatherLocation[], local: WeatherLocation[]): WeatherLocation[] {
  const merged = [...remote];
  for (const c of local) {
    if (!merged.some((m) => isSameCity(m, c))) merged.push(c);
  }
  return merged.slice(0, MAX_SAVED_CITIES);
}

// ── Estado compartido (singleton de módulo) ─────────────────────────────────
//
// El dashboard monta DOS selectores a la vez (barra de escritorio + fila móvil,
// uno siempre oculto por CSS), y además los remonta al refrescar el tiempo. Con
// estado por instancia, cada gemelo tenía su lista, su hidratación y su freno de
// escritura: el oculto conservaba la ciudad recién borrada y la resucitaba
// machacando la cuenta con su copia rancia. Un único estado de módulo = una sola
// lista, una sola hidratación y un solo UPDATE pendiente, viva aunque el widget
// se remonte.
//
// Ese estado va SIEMPRE atado a un user_id (`currentUid`). Cambiar de cuenta lo
// tira entero: si no, el siguiente UPDATE escribía la lista del usuario anterior
// en el perfil del nuevo.

dropLegacyCache();

let currentUid: string | null = null;
let citiesState: WeatherLocation[] = [DEFAULT_LOCATION];
let hydrated = false;      // la lista de la cuenta ya llegó
let remoteBlocked = false; // la lectura remota falló: esta sesión no escribe la cuenta
let hydrating: Promise<void> | null = null;
let persistTimer: number | null = null;
const listeners = new Set<() => void>();

export function subscribeCities(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getCitiesSnapshot(): WeatherLocation[] {
  return citiesState;
}

function emit() {
  listeners.forEach((l) => l());
}

/** Cerrar sesión (o cambiar de usuario): fuera todo lo que había en memoria. */
export function resetCities(): void {
  if (persistTimer !== null) {
    window.clearTimeout(persistTimer);
    persistTimer = null;
  }
  currentUid = null;
  citiesState = [DEFAULT_LOCATION];
  hydrated = false;
  remoteBlocked = false;
  hydrating = null;
  emit();
}

function schedulePersist() {
  persistSavedCities(currentUid, citiesState);
  if (!hydrated || remoteBlocked || !currentUid) return;
  const uid = currentUid;
  if (persistTimer !== null) window.clearTimeout(persistTimer);
  // Con freno: añadir/quitar varias ciudades seguidas = un solo UPDATE.
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    // Si entre medias se cambió de cuenta, este UPDATE ya no es de nadie.
    if (currentUid !== uid) return;
    void persistRemoteCities(uid, citiesState);
  }, 800);
}

export function updateCities(updater: (prev: WeatherLocation[]) => WeatherLocation[]): void {
  const next = updater(citiesState);
  if (next === citiesState) return;
  citiesState = next.slice(0, MAX_SAVED_CITIES);
  emit();
  schedulePersist();
}

/**
 * Hidrata desde la cuenta UNA vez por usuario, da igual cuántos selectores monten.
 * Si llega otro `uid` que el de la última vez, se tira el estado y se vuelve a
 * empezar: es un cambio de cuenta.
 */
export function hydrateCitiesOnce(uid: string | null): Promise<void> {
  if (!uid) {
    // Sin sesión no hay lista de nadie que hidratar.
    if (currentUid !== null) resetCities();
    return Promise.resolve();
  }

  if (uid !== currentUid) {
    resetCities();
    currentUid = uid;
    // Caché de ESTE usuario: pinta ya, sin esperar a la red.
    citiesState = loadSavedCities(uid);
    emit();
  }

  if (!hydrating) {
    hydrating = (async () => {
      const remote = await loadRemoteCities(uid);
      if (currentUid !== uid) return; // cambió de cuenta mientras se leía

      if (!remote.ok) {
        // No se pudo leer: la caché local sigue mandando en este aparato, pero la
        // cuenta queda intocable hasta la próxima sesión. Escribir «a ciegas» aquí
        // era lo que reseteaba la lista guardada desde otro aparato.
        remoteBlocked = true;
        hydrated = true;
        return;
      }

      hydrated = true;
      if (remote.cities.length > 0) {
        // La cuenta manda, y detrás va lo que este aparato tuviera SUYO sin subir.
        updateCities((prev) => mergeCities(remote.cities, prev));
        return;
      }

      // Cuenta leída y sin lista: la cuenta está vacía y vacía se queda. Antes se
      // sembraba aquí con `citiesState`, y como `citiesState` venía de la caché
      // compartida del navegador, cada cuenta nueva estrenaba las ciudades de la
      // anterior. Se limpia a lo que haya por defecto y no se sube nada: será la
      // propia pantalla, al añadir su ciudad activa, la que escriba la primera vez.
      citiesState = [DEFAULT_LOCATION];
      persistSavedCities(uid, citiesState);
      emit();
    })();
  }
  return hydrating;
}
